import React, { useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import { DECISIONS_URL, writeHeaders } from "./userVerificationApi";

const DISABLE = {
  action: "disable",
  label: "Disable",
  variant: "danger",
  explanation: "Disables the user's account.",
};

const SET_UNDER_AUDIT = {
  action: "set_under_audit",
  label: "Set under audit",
  variant: "danger",
  explanation:
    "Puts the user under audit and ghosts their requests waiting for approval. New requests follow the existing request approval rules, including profile overrides and manual-review mode.",
};

const VET = {
  action: "vet",
  label: "Vet",
  variant: "success",
  explanation: "Marks the user as vetted, with your name, email and today's date as the vetted note.",
};

const VERDICTS_BY_OUTCOME = {
  under_audit: [
    {
      action: "lift",
      label: "Lift audit",
      variant: "success",
      explanation:
        "Lifts the audit this check set and also vets the user, unless they are vetted already. Requests the verification ghosted stay ghosted; unghost them from the request pages.",
    },
    {
      action: "keep",
      label: "Keep audit",
      variant: "secondary",
      explanation: "The user stays under audit.",
    },
    DISABLE,
  ],
  suspicious: [VET, SET_UNDER_AUDIT, DISABLE],
  no_action: [VET, SET_UNDER_AUDIT, DISABLE],
};

// The staff verdicts on an automated user verification decision, each confirmed in a dialog
// that takes an optional comment. Lift audit is offered only when the decision set the audit.
function UserVerificationActions({ decision, onComplete, vertical = false }) {
  const [pending, setPending] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!decision.awaiting_review) return null;

  const verdicts = (VERDICTS_BY_OUTCOME[decision.outcome] || []).filter(
    (verdict) => verdict.action !== "lift" || decision.applied_under_audit_at,
  );

  function open(verdict) {
    setPending(verdict);
    setComment("");
    setError(null);
  }

  function close() {
    if (!busy) setPending(null);
  }

  async function submit() {
    setBusy(true);
    try {
      const response = await fetch(`${DECISIONS_URL}/${decision.id}/${pending.action}`, {
        method: "POST",
        headers: writeHeaders(),
        body: JSON.stringify({ comment }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.error || "Could not record the verdict.");
        return;
      }
      setPending(null);
      onComplete?.();
    } catch {
      setError("Network error - please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className={`d-flex gap-1 ${vertical ? "flex-column" : "flex-wrap"}`}>
        {verdicts.map((verdict) => (
          <button
            key={verdict.action}
            type="button"
            className={`btn btn-sm text-nowrap btn-outline-${verdict.variant}`}
            onClick={() => open(verdict)}
          >
            {verdict.label}
          </button>
        ))}
      </div>

      <Modal show={!!pending} onHide={() => close()}>
        <Modal.Header closeButton>
          <Modal.Title>
            {pending?.label}: {decision.user_name || "this user"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{pending?.explanation}</p>
          <label htmlFor={`uv-comment-${decision.id}`} className="form-label text-muted small">
            Comment (optional)
          </label>
          <textarea
            id={`uv-comment-${decision.id}`}
            className="form-control form-control-sm"
            rows="3"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={busy}
          />
          {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={`btn btn-${pending?.variant}`} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : pending?.label}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

UserVerificationActions.propTypes = {
  decision: PropTypes.shape({
    id: PropTypes.number.isRequired,
    outcome: PropTypes.string,
    awaiting_review: PropTypes.bool,
    applied_under_audit_at: PropTypes.string,
    user_name: PropTypes.string,
  }).isRequired,
  onComplete: PropTypes.func,
  vertical: PropTypes.bool,
};

export default UserVerificationActions;
